// Media library. docs/next_session.md "content depth" / CLAUDE.md §11.
// Upload + browse R2 assets; the r2Key is what activity payloads reference.
// Staff tool, English chrome. Migrated to the console component system in
// M7c / P6 — the GRID stays (a gallery is the right shape for media), but the
// header, filters and the loading/empty/error states now come from ~/components.

import { Badge, Button } from '@fajrarabi/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useRef, useState } from 'react';

import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  FilterBar,
  PageHeader,
  RowActions,
  SearchInput,
  useDebounced,
} from '~/components';
import { ApiError } from '~/lib/api';
import {
  type MediaAsset,
  type MediaKind,
  MEDIA_ACCEPT,
  deleteMedia,
  listMedia,
  mediaUrl,
  uploadMedia,
} from '~/lib/media';

export const Route = createFileRoute('/_authed/media')({
  component: MediaLibraryPage,
});

const KINDS: MediaKind[] = ['image', 'audio', 'video', 'pdf'];

function MediaLibraryPage() {
  const qc = useQueryClient();
  const [kind, setKind] = useState<MediaKind | 'all'>('all');
  const [q, setQ] = useState('');
  // Debounced so typing a key does not fire a request per keystroke.
  const debouncedQ = useDebounced(q, 300);

  const mediaQuery = useQuery({
    queryKey: ['media', kind, debouncedQ],
    queryFn: ({ signal }) =>
      listMedia({ kind: kind === 'all' ? undefined : kind, q: debouncedQ || undefined }, signal),
    staleTime: 3_000,
  });
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['media'] });

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title="Media library"
        description="Images, audio, video and worksheets. The R2 key is what activity payloads reference."
        actions={<UploadButton onUploaded={invalidate} />}
      />

      <FilterBar className="mb-4">
        <FilterChip label="All" active={kind === 'all'} onClick={() => setKind('all')} />
        {KINDS.map((k) => (
          <FilterChip key={k} label={k} active={kind === k} onClick={() => setKind(k)} />
        ))}
        <SearchInput
          className="ms-auto"
          value={q}
          onChange={setQ}
          placeholder="Search by key…"
          label="Search media by key"
        />
      </FilterBar>

      <div className="rounded-xl border border-admin-200 bg-white p-4">
        {mediaQuery.isLoading ? (
          // Skeleton tiles rather than a "Loading…" line — the grid keeps its
          // shape, so nothing jumps when the real assets arrive.
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
            {Array.from({ length: 8 }, (_, i) => (
              <div key={i} className="aspect-square animate-pulse rounded-xl bg-admin-100" />
            ))}
          </div>
        ) : mediaQuery.error ? (
          <ErrorState error={mediaQuery.error} onRetry={() => void mediaQuery.refetch()} />
        ) : mediaQuery.data && mediaQuery.data.length > 0 ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
            {mediaQuery.data.map((m) => (
              <MediaCard key={m.id} asset={m} onDeleted={invalidate} />
            ))}
          </div>
        ) : (
          <EmptyState
            icon="image"
            title={q || kind !== 'all' ? 'No media matches these filters' : 'No media yet'}
            description={
              q || kind !== 'all'
                ? 'Try a different search or file type.'
                : 'Upload an image, audio clip, video or worksheet to get started.'
            }
          />
        )}
      </div>
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-lg px-3 py-1.5 text-sm font-medium capitalize transition-colors ${
        active
          ? 'bg-brand-500 text-white'
          : 'border border-admin-200 bg-white text-admin-700 hover:bg-admin-50'
      }`}
    >
      {label}
    </button>
  );
}

// A reusable upload control (also used by the picker).
export function UploadButton({
  onUploaded,
  defaultKind,
  compact,
}: {
  onUploaded: (asset: MediaAsset) => void;
  defaultKind?: MediaKind;
  compact?: boolean;
}) {
  const [kind, setKind] = useState<MediaKind>(defaultKind ?? 'image');
  const fileRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (file: File) => uploadMedia({ file, kind }),
    onSuccess: (asset) => {
      setError(null);
      if (fileRef.current) fileRef.current.value = '';
      onUploaded(asset);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Upload failed.'),
  });

  return (
    <div className={compact ? '' : 'flex items-end gap-2'}>
      <div className="flex items-center gap-2">
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value as MediaKind)}
          className="rounded-2xl border border-burgundy-500/20 bg-white px-3 py-2 text-sm text-ink-900"
        >
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
        <input
          ref={fileRef}
          type="file"
          accept={MEDIA_ACCEPT[kind]}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) mut.mutate(f);
          }}
          className="hidden"
        />
        <Button onClick={() => fileRef.current?.click()} loading={mut.isPending} size="sm">
          Upload {kind}
        </Button>
      </div>
      {error && <p className="text-xs text-heart">{error}</p>}
    </div>
  );
}

function MediaCard({ asset, onDeleted }: { asset: MediaAsset; onDeleted: () => void }) {
  const [copied, setCopied] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const del = useMutation({
    mutationFn: () => deleteMedia(asset.id),
    onSuccess: onDeleted,
  });

  function copyKey(): void {
    void navigator.clipboard?.writeText(asset.r2Key).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-burgundy-500/10 bg-white">
      <div className="grid aspect-square place-items-center bg-cream-100/50">
        <MediaThumb asset={asset} />
      </div>
      <div className="p-3">
        <div className="flex items-center justify-between">
          <Badge tone="neutral">{asset.kind}</Badge>
          {asset.widthPx && asset.heightPx ? (
            <span className="text-[10px] text-ink-500">
              {asset.widthPx}×{asset.heightPx}
            </span>
          ) : asset.sizeBytes ? (
            <span className="text-[10px] text-ink-500">{fmtBytes(asset.sizeBytes)}</span>
          ) : null}
        </div>
        <button
          type="button"
          onClick={copyKey}
          title={asset.r2Key}
          className="mt-2 block w-full truncate text-start font-mono text-[11px] text-burgundy-600 hover:underline"
        >
          {copied ? 'Copied!' : asset.r2Key}
        </button>
        {/* Same row-action shape the tables use, so a card and a row offer
            actions the same way. */}
        <div className="mt-2 flex justify-end">
          <RowActions
            label={asset.r2Key}
            primary={{ id: 'copy', label: 'Copy R2 key', icon: 'copy', onSelect: copyKey }}
            actions={[
              {
                id: 'delete',
                label: 'Delete asset',
                icon: 'trash',
                destructive: true,
                onSelect: () => setConfirming(true),
              },
            ]}
          />
        </div>

        <ConfirmDialog
          open={confirming}
          onOpenChange={setConfirming}
          title="Delete this asset"
          description={
            <>
              <span className="font-mono text-xs">{asset.r2Key}</span> is removed from R2. 🔴 Any
              activity payload referencing this key will break — the reference is by key, not by a
              foreign key the database can protect.
            </>
          }
          confirmLabel="Delete asset"
          tone="danger"
          onConfirm={async () => {
            await del.mutateAsync();
          }}
        />
      </div>
    </div>
  );
}

export function MediaThumb({ asset }: { asset: MediaAsset }) {
  const url = mediaUrl(asset.r2Key);
  if (asset.kind === 'image') {
    return <img src={url} alt="" className="h-full w-full object-contain" loading="lazy" />;
  }
  const icon = asset.kind === 'audio' ? '🔊' : asset.kind === 'video' ? '🎬' : '📄';
  return <span className="text-5xl">{icon}</span>;
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
